/**
 * A pipe organ, synthesised, playing the hymnal.
 *
 * An organ is the one instrument a browser can imitate honestly, because a
 * real one *is* additive synthesis: each drawbar opens a rank of pipes at a
 * fixed harmonic of the note, and the sound is their sum. Sine oscillators at
 * those same ratios are not an approximation of an organ, they are the same
 * arithmetic with a different pipe. That is why this sounds like an instrument
 * where the earlier attempt at a human voice never could.
 *
 * Notes are scheduled ahead of time against the audio clock rather than fired
 * from timers. `setTimeout` drifts by tens of milliseconds under load, which on
 * a sustained chord is audible as a smear; the Web Audio clock does not.
 */

import { HYMNS_BY_ID } from '../data/hymns.js';

/**
 * Overall level. Music behind study wants to be present but ignorable.
 *
 * "Ignorable" had gone too far: measured, a hymn played at -22.3 dBFS peak.
 * This is something somebody chose to play by pressing a play button, so it
 * has to be plainly audible on laptop speakers — unlike the chant bed, which
 * nobody asked for. Now about -12 dBFS, a normal level for foreground music.
 */
const MASTER_GAIN = 0.7;

/**
 * The registration — which ranks are open, and how loud.
 *
 * `[harmonic, gain]`. This is a Principal chorus: the octaves carry the tone,
 * the twelfth (3) and the tierce (5) are the ones that make it read as *organ*
 * rather than as a stack of flutes. They are quiet on purpose; open them up and
 * it turns into a fairground.
 */
const STOPS = [
  [1, 1.0],    // 8'  — the note itself
  [2, 0.5],    // 4'  — octave
  [3, 0.22],   // 2⅔' — twelfth
  [4, 0.16],   // 2'  — fifteenth
  [5, 0.06],   // 1⅗' — tierce
  [6, 0.05],
  [8, 0.03],
];

/** Cents of detune per rank, alternating, for the beating a real rank has. */
const DETUNE = 1.6;

const SEMITONES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** 'F#4' → Hz. */
export function frequencyOf(name) {
  const match = /^([A-G])(#|b)?(-?\d)$/.exec(name);
  if (!match) return 440;
  const [, letter, accidental, octave] = match;
  let semitone = SEMITONES[letter] + (accidental === '#' ? 1 : accidental === 'b' ? -1 : 0);
  const midi = semitone + (Number(octave) + 1) * 12;
  return 440 * 2 ** ((midi - 69) / 12);
}

/**
 * Chord symbol to the notes under the melody.
 *
 * Only the triads the hymnal actually uses. An unknown symbol returns null and
 * the harmony simply rests, which is a better failure than guessing at it.
 */
const TRIADS = {
  C: ['C3', 'E3', 'G3'],
  D: ['D3', 'F#3', 'A3'],
  Dm: ['D3', 'F3', 'A3'],
  E: ['E3', 'G#3', 'B3'],
  Em: ['E3', 'G3', 'B3'],
  F: ['F3', 'A3', 'C4'],
  G: ['G2', 'B2', 'D3'],
  A: ['A2', 'C#3', 'E3'],
  Am: ['A2', 'C3', 'E3'],
  Bm: ['B2', 'D3', 'F#3'],
};

function buildImpulse(ctx, seconds = 4.2, decay = 2.2) {
  const rate = ctx.sampleRate;
  const length = Math.floor(rate * seconds);
  const impulse = ctx.createBuffer(2, length, rate);

  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    let last = 0;
    for (let i = 0; i < length; i++) {
      const t = i / length;
      const envelope = (1 - t) ** decay;
      const white = Math.random() * 2 - 1;
      last = last * (0.6 + 0.35 * t) + white * (0.4 - 0.35 * t);
      data[i] = last * envelope;
    }
  }
  return impulse;
}

export class Hymnal {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.bus = null;
    this.voices = [];
    this.timer = 0;

    this.hymnId = null;
    this.level = 1;
    this.running = false;

    /** Where in the note list the scheduler has got to. */
    this.index = 0;
    /** Audio-clock time the next note begins. */
    this.at = 0;
    /** Audio-clock time the current hymn began, for the progress bar. */
    this.startedAt = 0;
    this.onAdvance = null;
    this.onFinish = null;
  }

  get supported() {
    return typeof window !== 'undefined' && !!(window.AudioContext ?? window.webkitAudioContext);
  }

  #build() {
    const AudioCtx = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioCtx) return false;

    const ctx = this.ctx ?? new AudioCtx();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.setValueAtTime(0.0001, ctx.currentTime);
    this.master.connect(ctx.destination);

    // A church is most of what an organ sounds like. Wet-heavy, but with real
    // dry signal underneath so the attacks stay defined.
    const convolver = ctx.createConvolver();
    convolver.buffer = buildImpulse(ctx);

    const wet = ctx.createGain();
    wet.gain.value = 0.7;
    const dry = ctx.createGain();
    dry.gain.value = 0.55;

    this.bus = ctx.createGain();
    this.bus.gain.value = 1;
    this.bus.connect(dry).connect(this.master);
    this.bus.connect(convolver).connect(wet).connect(this.master);

    return true;
  }

  /** One rank of pipes for one note. */
  #pipe(frequency, at, duration, gain) {
    const ctx = this.ctx;
    const nodes = [];

    STOPS.forEach(([harmonic, level], rank) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency * harmonic, at);
      // Alternating detune, so ranks beat against each other rather than
      // summing into one sterile tone.
      osc.detune.setValueAtTime(rank % 2 ? DETUNE : -DETUNE, at);

      const env = ctx.createGain();
      // A pipe speaks quickly but not instantly — the short ramp is the chiff,
      // and without it the attack clicks.
      const peak = Math.max(0.0002, gain * level);
      env.gain.setValueAtTime(0.0001, at);
      env.gain.exponentialRampToValueAtTime(peak, at + 0.045);
      env.gain.setValueAtTime(peak, at + Math.max(0.06, duration - 0.12));
      // Release into the reverb rather than stopping dead.
      env.gain.exponentialRampToValueAtTime(0.0001, at + duration + 0.18);

      osc.connect(env).connect(this.bus);
      osc.start(at);
      osc.stop(at + duration + 0.25);
      nodes.push(osc, env);

      osc.onended = () => {
        for (const node of nodes) {
          try {
            node.disconnect();
          } catch {
            /* already torn down */
          }
        }
        this.voices = this.voices.filter((v) => v !== osc);
      };
      this.voices.push(osc);
    });
  }

  /**
   * Schedule everything that starts in the next couple of seconds.
   *
   * Called on a timer, but the timer only decides *when we think about it* —
   * every note is placed on the audio clock, so a late tick changes nothing
   * about where the note lands.
   */
  #tick() {
    if (!this.running) return;
    const hymn = HYMNS_BY_ID[this.hymnId];
    if (!hymn) return;

    const horizon = this.ctx.currentTime + 2.5;
    const beat = 60 / hymn.tempo;

    while (this.at < horizon) {
      if (this.index >= hymn.notes.length) {
        // Round it off with a breath, then begin again. A hymnal that stops
        // after one tune is a hymnal you have to keep going back to.
        this.at += beat * 2;
        this.index = 0;
        this.startedAt = this.at;
        this.onFinish?.();
        continue;
      }

      const [pitch, beats, chord] = hymn.notes[this.index];
      const duration = beats * beat;

      if (chord) this.currentChord = chord;

      if (pitch) {
        // The melody sits above and a little louder; the harmony holds under
        // it. Anything closer and the tune stops being the thing you hear.
        this.#pipe(frequencyOf(pitch), this.at, duration * 0.94, 0.16);

        const triad = TRIADS[this.currentChord];
        if (triad) {
          for (const note of triad) {
            this.#pipe(frequencyOf(note), this.at, duration * 0.94, 0.052);
          }
          // The pedal: root, two octaves down, only on longer notes so it
          // marks the harmony rather than mudding every passing quaver.
          if (beats >= 2) {
            const root = frequencyOf(triad[0]) / 2;
            this.#pipe(root, this.at, duration * 0.94, 0.075);
          }
        }
      }

      this.at += duration;
      this.index += 1;
      this.onAdvance?.(this.index);
    }
  }

  /** Fraction through the current hymn, 0–1. */
  position() {
    const hymn = HYMNS_BY_ID[this.hymnId];
    if (!hymn || !this.ctx || !this.running) return 0;
    const beat = 60 / hymn.tempo;
    const total = hymn.notes.reduce((sum, [, beats]) => sum + beats, 0) * beat;
    const elapsed = this.ctx.currentTime - this.startedAt;
    return Math.max(0, Math.min(1, elapsed / total));
  }

  /** Which note is sounding, for the highlight in the panel. */
  playingIndex() {
    const hymn = HYMNS_BY_ID[this.hymnId];
    if (!hymn || !this.ctx || !this.running) return -1;
    const beat = 60 / hymn.tempo;
    let elapsed = this.ctx.currentTime - this.startedAt;
    if (elapsed < 0) return -1;
    for (const [index, [, beats]] of hymn.notes.entries()) {
      elapsed -= beats * beat;
      if (elapsed < 0) return index;
    }
    return -1;
  }

  async play(hymnId) {
    if (!this.#build()) return false;
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    if (hymnId && hymnId !== this.hymnId) this.stopVoices();
    this.hymnId = hymnId ?? this.hymnId;
    if (!HYMNS_BY_ID[this.hymnId]) return false;

    this.running = true;
    this.currentChord = null;
    this.index = 0;
    // A beat of air before the first chord, so the fade-in is not audible as
    // part of the tune.
    this.at = this.ctx.currentTime + 0.25;
    this.startedAt = this.at;

    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.setValueAtTime(Math.max(0.0001, this.master.gain.value), this.ctx.currentTime);
    this.master.gain.exponentialRampToValueAtTime(
      MASTER_GAIN * this.level,
      this.ctx.currentTime + 0.6,
    );

    this.#tick();
    clearInterval(this.timer);
    this.timer = setInterval(() => this.#tick(), 500);
    return true;
  }

  setLevel(level) {
    this.level = Math.max(0, Math.min(1, level));
    if (this.master && this.ctx && this.running) {
      this.master.gain.cancelScheduledValues(this.ctx.currentTime);
      this.master.gain.linearRampToValueAtTime(
        Math.max(0.0001, MASTER_GAIN * this.level),
        this.ctx.currentTime + 0.15,
      );
    }
  }

  stopVoices() {
    for (const osc of [...this.voices]) {
      try {
        osc.stop();
      } catch {
        /* already stopped */
      }
    }
    this.voices = [];
  }

  pause() {
    this.running = false;
    clearInterval(this.timer);
    if (this.master && this.ctx) {
      // Fade rather than cut: stopping an organ dead is a click, and the
      // reverb tail is most of what makes it sound like a room.
      this.master.gain.cancelScheduledValues(this.ctx.currentTime);
      this.master.gain.setValueAtTime(
        Math.max(0.0001, this.master.gain.value),
        this.ctx.currentTime,
      );
      this.master.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.4);
    }
    setTimeout(() => {
      if (!this.running) this.stopVoices();
    }, 500);
  }

  stop() {
    this.pause();
    this.index = 0;
  }
}
